import React, { useState } from 'react';
import { API_BASE } from '../config';

const RACES = ["Human","Elf","Dwarf","Halfling","Dragonborn","Gnome","Half-Elf","Half-Orc","Tiefling"];
const CLASSES = ["Barbarian","Bard","Cleric","Druid","Monk","Necromancer","Paladin","Ranger","Rogue","Deathknight","Sorcerer","Warlock","Warrior","Wizard"];
const SUBCLASSES = {"Barbarian":["Berserker","Totem Warden"],"Bard":["Songweaver","Warbard"],"Cleric":["Holy","Discipline","Shadow"],"Druid":["Restoration","Feral","Balance"],"Warrior":["Arms","Fury","Protection"],"Monk":["Windwalker","Shadowstep","Elementalist"],"Necromancer":["Reanimator","Harvester","Lichborn"],"Paladin":["Protection","Holy","Retribution"],"Ranger":["Marksmanship","Beast Mastery","Survival"],"Rogue":["Subtlety","Assassination","Outlaw"],"Deathknight":["Blood","Unholy","Frost"],"Sorcerer":["Arcane","Frost","Fire"],"Warlock":["Demonology","Affliction","Destruction"],"Wizard":["Divination","Enchantment","Conjuration"]};
const BACKGROUNDS = ["Acolyte","Criminal","Folk Hero","Noble","Outlander","Sage","Soldier","Charlatan","Entertainer","Hermit"];
const ABILITIES = ["Strength","Dexterity","Constitution","Intelligence","Wisdom","Charisma"];

function roll4d6DropLowest() {
  const rolls = Array.from({length: 4}, () => Math.floor(Math.random() * 6) + 1);
  rolls.sort((a, b) => a - b);
  return { rolls, total: rolls[1] + rolls[2] + rolls[3], dropped: rolls[0] };
}

function modifier(score) {
  const mod = Math.floor((score - 10) / 2);
  return mod >= 0 ? `+${mod}` : `${mod}`;
}

export default function CharacterCreator({ roomId, onComplete }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [race, setRace] = useState('');
  const [charClass, setCharClass] = useState('');
  const [subclass, setSubclass] = useState('');
  const [background, setBackground] = useState('');
  const [scores, setScores] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [diceDisplay, setDiceDisplay] = useState(null);
  const [rerollUsed, setRerollUsed] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const rollAllScores = () => {
    setRolling(true);
    setDiceDisplay(null);
    // Animate dice rolling
    let count = 0;
    const interval = setInterval(() => {
      setDiceDisplay(ABILITIES.map(() => ({ rolls: Array.from({length:4}, () => Math.floor(Math.random()*6)+1), total: '?', dropped: 0 })));
      count++;
      if (count >= 8) {
        clearInterval(interval);
        const results = ABILITIES.map(a => ({ ability: a, ...roll4d6DropLowest() }));
        setDiceDisplay(results);
        setScores(results);
        setRolling(false);
      }
    }, 120);
  };

  const handleReroll = () => {
    setRerollUsed(true);
    rollAllScores();
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const token = localStorage.getItem('session');
    const scoreMap = {};
    scores.forEach(s => { scoreMap[s.ability.toLowerCase()] = s.total; });
    const conMod = Math.floor((scoreMap.constitution - 10) / 2);

    const body = {
      action: 'create_character',
      room_id: roomId,
      char_name: name,
      race, class: charClass, subclass: subclass || null, background,
      ...scoreMap,
      level: 1,
      hp: 10 + conMod,
      max_hp: 10 + conMod,
      ac: 10 + Math.floor((scoreMap.dexterity - 10) / 2),
    };

    try {
      const res = await fetch(`${API_BASE}/room/create-character`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save.'); setSaving(false); return; }
      onComplete(data);
    } catch { setError('Network error.'); }
    setSaving(false);
  };

  return (
    <div className="char-creator">
      <div className="char-creator-card">
        <div className="cc-progress">
          {[1,2,3,4,5,6].map(s => <div key={s} className={`cc-dot ${step >= s ? 'active' : ''}`} />)}
        </div>

        {/* Step 1: Name */}
        {step === 1 && (
          <div className="cc-step">
            <h2>Step 1 — Name Your Hero</h2>
            <p>What shall the bards sing of?</p>
            <input className="login-input cc-input" value={name} onChange={e => setName(e.target.value)} placeholder="Enter character name..." maxLength={30} autoFocus />
            <button className="login-submit" onClick={() => setStep(2)} disabled={name.trim().length < 2}>Continue</button>
          </div>
        )}

        {/* Step 2: Race */}
        {step === 2 && (
          <div className="cc-step">
            <h2>Step 2 — Choose Your Race</h2>
            <p>Your blood, your heritage.</p>
            <div className="cc-grid">
              {RACES.map(r => (
                <div key={r} className={`cc-option ${race === r ? 'selected' : ''}`} onClick={() => setRace(r)}>{r}</div>
              ))}
            </div>
            <button className="login-submit" onClick={() => setStep(3)} disabled={!race}>Continue</button>
          </div>
        )}

        {/* Step 3: Class */}
        {step === 3 && (
          <div className="cc-step">
            <h2>Step 3 — Choose Your Class</h2>
            <p>How will you fight?</p>
            <div className="cc-grid">
              {CLASSES.map(c => (
                <div key={c} className={`cc-option ${charClass === c ? 'selected' : ''}`} onClick={() => { setCharClass(c); setSubclass(''); }}>{c}</div>
              ))}
            </div>
            <button className="login-submit" onClick={() => setStep(SUBCLASSES[charClass] ? 4 : 5)} disabled={!charClass}>Continue</button>
          </div>
        )}

        {/* Step 4: Subclass */}
        {step === 4 && (
          <div className="cc-step">
            <h2>Step 4 — Specialization</h2>
            <p>Refine your path.</p>
            <div className="cc-grid">
              {(SUBCLASSES[charClass] || []).map(s => (
                <div key={s} className={`cc-option ${subclass === s ? 'selected' : ''}`} onClick={() => setSubclass(s)}>{s}</div>
              ))}
            </div>
            <button className="login-submit" onClick={() => setStep(5)} disabled={!subclass}>Continue</button>
          </div>
        )}

        {/* Step 5: Background */}
        {step === 5 && (
          <div className="cc-step">
            <h2>Step 5 — Background</h2>
            <p>Where did you come from?</p>
            <div className="cc-grid">
              {BACKGROUNDS.map(b => (
                <div key={b} className={`cc-option ${background === b ? 'selected' : ''}`} onClick={() => setBackground(b)}>{b}</div>
              ))}
            </div>
            <button className="login-submit" onClick={() => setStep(6)} disabled={!background}>Continue</button>
          </div>
        )}

        {/* Step 6: Ability Scores — Dice Rolling */}
        {step === 6 && (
          <div className="cc-step">
            <h2>Step 6 — Roll Your Fate</h2>
            <p>4d6, drop the lowest. Click the dice to roll.</p>

            {!scores && (
              <div className="cc-dice-area" onClick={!rolling ? rollAllScores : undefined}>
                <div className={`cc-dice ${rolling ? 'rolling' : ''}`}>🎲</div>
                <div className={`cc-dice ${rolling ? 'rolling' : ''}`}>🎲</div>
                <p className="cc-dice-hint">{rolling ? 'Rolling...' : 'Click to roll!'}</p>
              </div>
            )}

            {diceDisplay && scores && (
              <div className="cc-scores">
                {scores.map((s, i) => (
                  <div key={i} className="cc-score-row">
                    <span className="cc-ability">{s.ability}</span>
                    <span className="cc-rolls">[{s.rolls.map((r, j) => <span key={j} className={j === 0 ? 'dropped' : ''}>{r}</span>)}]</span>
                    <span className="cc-total">{s.total}</span>
                    <span className="cc-mod">{modifier(s.total)}</span>
                  </div>
                ))}
                {!rerollUsed && (
                  <button className="cc-reroll" onClick={handleReroll}>🎲 Reroll (1 free)</button>
                )}
                <button className="login-submit" onClick={handleSave} disabled={saving}>{saving ? 'Creating...' : '⚔️ Create Character'}</button>
              </div>
            )}

            {error && <p className="login-error">{error}</p>}
          </div>
        )}

        {step > 1 && step <= 6 && !scores && (
          <button className="cc-back" onClick={() => setStep(step - 1)}>← Back</button>
        )}
      </div>
    </div>
  );
}
